import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LifeBuoy, BookOpen, TicketPlus, Ticket, ChevronRight, Paperclip, MessageSquare, Inbox } from "lucide-react";
import { api } from "../lib/api";
import { useLiveSync } from "../lib/userEvents";
import { SupportFaq } from "../components/SupportFaq";
import { SupportAgentsSection } from "../components/SupportAgents";
import { SupportTicketForm } from "../components/SupportTicketForm";
import { StatusBadge, fmtDate } from "../components/SupportTicketBits";

const TABS = [["faq", "FAQ", BookOpen], ["new", "Buat Tiket", TicketPlus], ["tickets", "Tiket Saya", Ticket]];

function TicketRow({ t, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className={`flex w-full items-start gap-3 rounded-2xl border bg-white p-4 text-left transition hover:border-[#2F6BFF]/40 hover:shadow-sm ${t.unread_user ? "border-[#2F6BFF]/40" : "border-[#E6EAF2]"}`} data-testid={`ticket-row-${t.id}`}>
      <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${t.unread_user ? "bg-[#2F6BFF]" : "bg-transparent"}`} />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[11px] font-bold text-slate-400" data-no-i18n="true">{t.number}</span>
          <StatusBadge status={t.status} label={t.status_label} />
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{t.category_label}</span>
        </span>
        <span className="mt-1 block truncate text-sm font-semibold text-slate-800" data-no-i18n="true">{t.subject}</span>
        <span className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
          <span>Diperbarui {fmtDate(t.updated_at)}</span>
          {t.message_count > 0 && <span className="flex items-center gap-1"><MessageSquare size={11} /> {t.message_count}</span>}
          {t.attachments?.length > 0 && <span className="flex items-center gap-1"><Paperclip size={11} /> {t.attachments.length}</span>}
          {t.assignee_name && <span data-no-i18n="true">Ditangani: {t.assignee_name}</span>}
        </span>
      </span>
      <ChevronRight size={16} className="mt-1 shrink-0 text-slate-300" />
    </button>
  );
}

function MyTickets() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("active");
  useLiveSync(() => api.get(`/support/tickets?status=${filter}`).then((r) => setItems(r.data.items)).catch(() => setItems([])), ["notification"], 30000, [filter]);
  return (
    <div className="space-y-3" data-testid="my-tickets">
      <div className="flex gap-2">
        {[["active", "Aktif"], ["done", "Selesai"], ["", "Semua"]].map(([v, l]) => <button key={v} type="button" onClick={() => { setItems(null); setFilter(v); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${filter === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-[#E6EAF2] hover:bg-slate-50"}`} data-testid={`tickets-filter-${v || "all"}`}>{l}</button>)}
      </div>
      {items === null ? <p className="p-6 text-sm text-slate-400">Memuat tiket…</p>
        : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white/60 p-8 text-center" data-testid="tickets-empty">
            <Inbox size={28} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm font-semibold text-slate-700">Belum ada tiket di sini</p>
            <p className="mt-1 text-xs text-slate-500">Semua laporan dan percakapanmu dengan tim support akan muncul di daftar ini.</p>
          </div>
        ) : items.map((t) => <TicketRow key={t.id} t={t} onOpen={() => nav(`/support/tickets/${t.id}`)} />)}
    </div>
  );
}

export default function Support() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = TABS.some(([k]) => k === sp.get("tab")) ? sp.get("tab") : "faq";
  const setTab = (k) => setSp(k === "faq" ? {} : { tab: k });
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8" data-testid="support-page">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-md" style={{ background: "linear-gradient(135deg,#2F6BFF,#7C3AED)" }}><LifeBuoy size={22} /></span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Pusat Bantuan</h1>
            <p className="text-sm text-slate-500">Jawaban cepat lewat FAQ, atau kirim tiket ke tim support Oryntix.</p>
          </div>
        </div>
        <div className="flex gap-1 rounded-2xl bg-white p-1 ring-1 ring-[#E6EAF2]" data-testid="support-tabs">
          {TABS.map(([k, l, I]) => <button key={k} type="button" onClick={() => setTab(k)} className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition sm:flex-none sm:px-4 ${tab === k ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`support-tab-${k}`}><I size={14} /> {l}</button>)}
        </div>
      </div>
      {tab === "faq" && <div className="mb-6" data-testid="support-agents-block"><SupportAgentsSection /></div>}
      {tab === "faq" && <SupportFaq initialId={sp.get("faq") || ""} onAsk={() => setTab("new")} />}
      {tab === "new" && <div className="aivora-card p-5 sm:p-6"><SupportTicketForm onCreated={(t) => nav(`/support/tickets/${t.id}`)} /></div>}
      {tab === "tickets" && <MyTickets />}
    </div>
  );
}
